/**
 * Takeover API Types
 *
 * Core type definitions for the API layer.
 * These types represent the data structures used throughout the application.
 */

// =============================================================================
// Primitive Types
// =============================================================================

/** Ethereum address (checksummed) */
export type Address = `0x${string}`;

/** Pool ID (bytes32 from Uniswap V4) */
export type PoolId = `0x${string}`;

/** BigInt serialized as string for JSON transport */
export type BigIntString = string;

// =============================================================================
// Core Entities
// =============================================================================

/**
 * A seat represents 1% ownership of a pool's fee distribution.
 * Each pool has exactly 100 seats (indices 0-99).
 */
export interface Seat {
  id: string;
  poolId: PoolId;
  seatIndex: number;
  holder: Address | null;
  selfAssessedPrice: BigIntString;
  balance: BigIntString;
  lastUpdateTime: number;
  feeDebtPerShare: BigIntString;
  lastPriceChangeTime: number;
  isActive: boolean;
  createdAt: number;
}

/**
 * Computed seat metrics for UI display.
 * These values are calculated from seat state + current time.
 */
export interface SeatMetrics {
  /** Current balance after pending taxes (USDC, 6 decimals) */
  currentBalance: BigIntString;
  /** Pending ETH fees available to claim */
  pendingFees: BigIntString;
  /** Weekly tax at current price (USDC, 6 decimals) */
  weeklyTax: BigIntString;
  /** Seconds until seat forfeits (0 if already forfeited) */
  timeUntilForfeiture: number;
  /** Seconds until price can be changed (0 if ready) */
  cooldownRemaining: number;
  /** Runway status: 'healthy' | 'warning' | 'critical' | 'danger' */
  runwayStatus: RunwayStatus;
}

export type RunwayStatus = "healthy" | "warning" | "critical" | "danger";

/**
 * A pool represents a token's fee distribution system.
 * Each pool has 100 seats for Harberger-taxed fee sharing.
 */
export interface Pool {
  id: PoolId;
  tokenId?: string;
  /** Address of the creator who deposited the token */
  creatorAddress?: string;
  // Token metadata
  name?: string;
  symbol?: string;
  imageUrl?: string;
  memecoinAddress?: string;
  /** Flaunch NFT contract that minted this pool's token */
  flaunchAddress?: string;
  /** Contract address of the manager that owns this pool (main or legacy) */
  managerAddress?: string;
  // Computed metrics (indexed)
  takeoverCost: BigIntString;
  floorPrice: BigIntString;
  averagePrice: BigIntString;
  ceilingPrice: BigIntString;
  // Discovery signals
  uniqueHolderCount?: number;
  firstSeatClaimedAt?: number | null;
  // Core fields
  activeSeatCount: number;
  isGraduated: boolean;
  graduatedAt: number | null;
  feeAccumulator: BigIntString;
  lastProcessedFees: BigIntString;
  totalTaxCollected: BigIntString;
  totalFeesDistributed: BigIntString;
  createdAt: number;
  // Graduation fields
  graduationPhase: 'none' | 'pre' | 'graduated';
  graduationManagerAddress?: string;
  mintPrice?: BigIntString;
  seatsPurchasedFromCreator: number;
  referrer?: string;
  referrerBps?: number;
  totalCreatorEarnings: BigIntString;
  keeperReserve: BigIntString;
  lastSeatPurchasedAt?: number | null;
  // Reward tracking (from FeeEscrow Deposit events)
  totalRewardsEth?: BigIntString;
  totalRewardEvents?: number;
  lastRewardAt?: number | null;
  /** Unix sec when materialized reward rollups last advanced (V1: fee deposit; V2: YieldReceived). */
  yield7dUpdatedAt?: number | null;
  // V1 rolling yield & TVL (from FeeEscrow Deposit events)
  yield7d?: BigIntString;
  /** Materialized trailing rewards: hourly/daily UTC buckets (ETH wei for V1, yield token for V2) */
  rewards1d?: BigIntString;
  rewards7d?: BigIntString;
  tvlUsd?: BigIntString;
  // V2 grid markers (set when pool originates from a V2 Grid entity)
  isV2Grid?: boolean;
  v2YieldToken?: string;
  v2YieldTokenDecimals?: number;
  taxRateBps?: number;
  taxToken?: string;
  taxTokenDecimals?: number;
  totalSeats?: number;
  // Social links
  twitterUrl?: string;
  telegramUrl?: string;
  discordUrl?: string;
  websiteUrl?: string;
}

/**
 * A reward event from the FeeEscrow Deposit.
 * Tracks per-pool ETH fees from trading activity.
 */
export interface RewardEvent {
  id: string;
  poolId: PoolId;
  payee: string;
  amount: BigIntString;
  timestamp: number;
  blockNumber: number;
  txHash: string;
}

/**
 * A buyback event where collected USDC tax revenue was used to buy back tokens.
 */
export interface BuybackEvent {
  id: string;
  usdcSpent: BigIntString;
  tokensReceived: BigIntString;
  receiver: string;
  timestamp: number;
  blockNumber: number;
  txHash: string;
}

/**
 * A keeper-executed buyback event via the TakeoverBuybackKeeper contract.
 */
export interface KeeperBuybackEvent {
  id: string;
  keeper: string;
  keeperReward: BigIntString;
  usdcIn: BigIntString;
  totalTakeoverOut: BigIntString;
  timestamp: number;
  blockNumber: number;
  txHash: string;
}

/**
 * Per-keeper aggregate stats. Powers the buyback leaderboard.
 */
export interface KeeperProfile {
  id: string;
  totalBuybacks: number;
  totalKeeperRewards: BigIntString;
  totalUsdcIn: BigIntString;
  totalTakeoverOut: BigIntString;
  lastBuybackAt: number | null;
}

export interface V2KeeperBuybackEvent {
  id: string;
  gridId: string;
  keeper: string;
  keeperReward: BigIntString;
  amountIn: BigIntString;
  amountOut: BigIntString;
  timestamp: number;
  blockNumber: number;
  txHash: string;
}

/**
 * A protocol tax withdrawal event.
 */
export interface ProtocolTaxWithdrawal {
  id: string;
  amount: BigIntString;
  timestamp: number;
  blockNumber: number;
  txHash: string;
}

/**
 * Extended pool data with seat data for grid display.
 * Note: takeoverCost, floorPrice, etc. are now in Pool itself (indexed).
 */
export interface PoolWithMetrics extends Pool {
  /** 24h trading volume (computed separately) */
  volume24h?: BigIntString;
  /** All 100 seats (for grid display) */
  seats?: Seat[];
  /** Lightweight seat indices for mini-grid display (sorted) */
  filledSeatIndices?: number[];
  /** Seat holders with their seat indices for grid avatars */
  seatHolders?: { seatIndex: number; holder: string; selfAssessedPrice?: string; heldSince?: number; deposit?: string; lastTaxTime?: number }[];
  /** 24h change in floor price (USDC, 6 decimals) — positive = increased */
  floorPriceChange24h?: BigIntString;
  /** 24h change in floor price as percentage (e.g. 12.5 = +12.5%). null if no snapshot */
  floorPriceChangePercent24h?: number | null;

  /** V2 UGM grid markers (set when item originates from a V2 Grid entity) */
  isV2Grid?: boolean;
  v2GridId?: string;
  v2AdapterType?: 'flaunch' | 'uniswap-v3' | 'uniswap-v4' | 'aerodrome';
  v2YieldToken?: string;
  v2YieldTokenDecimals?: number;
  /** Rolling 7-day yield received (raw bigint string, in yield token units) */
  v2Yield7d?: string;
  /** Grid-level TVL in USD, 6 decimals (from indexer totalTvlUsd) */
  v2TotalTvlUsd?: string;
  v2Assets?: Array<{
    id: string;
    active: boolean;
    assetType: string | null;
    nftContract: string | null;
    nftTokenId: string | null;
    token0Symbol: string | null;
    token1Symbol: string | null;
    token0Address: string | null;
    token1Address: string | null;
    isFullRange: boolean | null;
    flaunchAddress: string | null;
  }>;
  /** Human-readable pair label, e.g. "USDC/TESTOVER LP" */
  pairLabel?: string;
  /** V2 tax rate in basis points (e.g. 500 = 5%) for client-side forfeit computation */
  v2TaxRateBps?: number;
}

/**
 * A user who interacts with the Takeover system.
 */
export interface User {
  id: Address;
  totalDeposited: BigIntString;
  totalWithdrawn: BigIntString;
  totalTaxesPaid: BigIntString;
  totalFeesClaimed: BigIntString;
  seatCount: number;
  seatsSold: number;
  totalSeatValue: BigIntString;
  points: BigIntString;
  createdAt: number;
  v1SeatCount: number;
  v2SeatCount: number;
  v1TotalDeposited: BigIntString;
  v2TotalDeposited: BigIntString;
  v1TotalWithdrawn: BigIntString;
  v2TotalWithdrawn: BigIntString;
  v1TotalTaxesPaid: BigIntString;
  v2TotalTaxesPaid: BigIntString;
  v1TotalFeesEarned: BigIntString;
  v2FeesEarnedUsd: BigIntString;
  v2CreatorFeesEarnedUsd: BigIntString;
  totalCreatorFeesEarned: BigIntString;
  v1TotalCreatorFeesEarned: BigIntString;
  totalDepositedUsd: BigIntString;
  totalWithdrawnUsd: BigIntString;
  totalTaxesPaidUsd: BigIntString;
  totalSeatValueUsd: BigIntString;
  v2TotalDepositedUsd: BigIntString;
  v2TotalWithdrawnUsd: BigIntString;
  v2TotalTaxesPaidUsd: BigIntString;
}

/**
 * Unified Position entity — lightweight cross-version view of Pools and Grids.
 */
export interface Position {
  id: string;
  version: 'v1' | 'v2';
  creator: Address;
  name?: string;
  symbol?: string;
  imageUrl?: string;
  activeSeatCount: number;
  totalSeats: number;
  takeoverCost: BigIntString;
  floorPrice: BigIntString;
  averagePrice: BigIntString;
  ceilingPrice: BigIntString;
  uniqueHolderCount: number;
  graduated: boolean;
  createdAt: number;
  lastSeatPurchasedAt: number | null;
  totalRewards: BigIntString;
  poolId?: string;
  gridId?: string;
}

/**
 * User portfolio with computed winnings.
 */
export interface UserPortfolio extends User {
  /** Net winnings: fees - taxes + buyout gains - buyout losses */
  netWinnings: BigIntString;
  /** All seats held by user */
  seats: SeatWithPool[];
  /** Seats requiring attention (low balance) */
  alertSeats: SeatAlert[];
}

export interface SeatWithPool extends Seat {
  pool: Pool;
  metrics: SeatMetrics;
}

export interface SeatAlert {
  seat: SeatWithPool;
  type: "low_balance_7d" | "low_balance_24h" | "forfeiture_imminent";
  message: string;
}

// =============================================================================
// Event History
// =============================================================================

export type SeatEventType =
  | "CLAIMED"
  | "BOUGHT"
  | "ABANDONED"
  | "FORFEITED"
  | "PRICE_UPDATED"
  | "DEPOSIT_ADDED"
  | "BALANCE_WITHDRAWN"
  | "CREATOR_FEES_WITHDRAWN"
  | "FEES_ACCRUED"
  | "FEES_CLAIMED"
  | "TAX_POKED";

export interface SeatEvent {
  id: string;
  seatId: string;
  poolId: PoolId;
  eventType: SeatEventType;
  user: Address;
  timestamp: number;
  blockNumber: number;
  txHash: string;
  price?: BigIntString;
  amount?: BigIntString;
  newBalance?: BigIntString;
  oldHolder?: Address;
  newHolder?: Address;
  refund?: BigIntString;
  newPrice?: BigIntString;
}

// =============================================================================
// Protocol Stats
// =============================================================================

export interface ProtocolStats {
  totalSeatsEverClaimed: number;
  totalActiveSeats: number;
  totalProtocolTaxCollected: BigIntString;
  totalProtocolTaxWithdrawn: BigIntString;
  currentTaxRatePerWeek: number;
  lastTaxRateUpdate: number | null;
  totalPools: number;
  totalGraduatedPools: number;
  /** Total ETH swap fees deposited across all pools (18 decimals) */
  totalSwapFeesEth: BigIntString;
  /** Total ETH fees claimed by seat holders (18 decimals) */
  totalFeesClaimedEth: BigIntString;
}

// =============================================================================
// API Response Types
// =============================================================================

export interface ApiResponse<T> {
  data: T;
  meta: {
    timestamp: number;
    cached: boolean;
    cacheAge?: number;
  };
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasMore: boolean;
  };
  meta: {
    timestamp: number;
    cached: boolean;
    cacheAge?: number;
  };
}

// =============================================================================
// API Request Types
// =============================================================================

export interface PoolsQueryParams {
  /** Discovery tab — determines sort strategy */
  tab?: "top" | "new" | "trending" | "earners";
  /** Filter by graduation status */
  graduated?: boolean;
  /** Sort field (used when tab is not set) */
  sortBy?: "activeSeatCount" | "takeoverCost" | "volume24h" | "createdAt" | "totalRewardsEth";
  /** Sort direction */
  sortOrder?: "asc" | "desc";
  /** Page number (1-indexed) */
  page?: number;
  /** Items per page */
  pageSize?: number;
  /** Search by pool name/symbol */
  search?: string;
  /** Include filled seat indices for mini-grid display */
  includeSeatIndices?: boolean;
  /** Exclude buried (low-velocity) pools. Default true for browse, false for search. */
  excludeBuried?: boolean;
}

export interface PortfolioQueryParams {
  /** Include seat metrics (more expensive) */
  includeMetrics?: boolean;
  /** Include low balance alerts */
  includeAlerts?: boolean;
}

// =============================================================================
// Real-time Event Types (SSE)
// =============================================================================

export type LiveEventType =
  | "seat_claimed"
  | "seat_bought"
  | "seat_forfeited"
  | "price_updated"
  | "pool_graduated"
  | "balance_updated";

export interface LiveEvent {
  type: LiveEventType;
  timestamp: number;
  data: SeatEvent | Pool;
}

// =============================================================================
// Cache Key Types
// =============================================================================

export const CacheKeys = {
  pool: (id: PoolId) => `pool:${id}`,
  poolWithSeats: (id: PoolId) => `pool:${id}:seats`,
  pools: (params: string) => `pools:${params}`,
  seat: (poolId: PoolId, seatIndex: number) => `seat:${poolId}:${seatIndex}`,
  user: (address: Address) => `user:${address.toLowerCase()}`,
  userPortfolio: (address: Address) => `portfolio:${address.toLowerCase()}`,
  protocolStats: () => "protocol:stats",
  seatEvents: (poolId: PoolId, seatIndex: number) =>
    `events:${poolId}:${seatIndex}`,
} as const;

// =============================================================================
// Contract Constants
// =============================================================================

export const CONSTANTS = {
  TOTAL_SEATS: 100,
  SEAT_SHARE_BPS: 100, // 1% = 100 basis points
  PRICE_CHANGE_COOLDOWN: 20 * 60, // 20 minutes in seconds
  USDC_DECIMALS: 6,
  TAX_RATE_DENOMINATOR: 10000,
  SECONDS_PER_WEEK: 7 * 24 * 60 * 60,
  // Runway thresholds (in seconds)
  // Warning/flag only shows at 4 days or less
  RUNWAY_HEALTHY: 4 * 24 * 60 * 60, // 4 days
  RUNWAY_WARNING: 3 * 24 * 60 * 60, // 3 days
  RUNWAY_CRITICAL: 2 * 24 * 60 * 60, // 2 days
} as const;

export const MINT_PRICE_PRESETS = [1, 10, 50, 100] as const;
export const MINT_PRICE_PRESETS_TESTNET = [0.001, 1, 10, 50] as const;
export const MINT_PRICE_PRESETS_USDC = [1_000_000n, 10_000_000n, 50_000_000n, 100_000_000n] as const;

export function getMintPricePresets(): readonly number[] {
  const chainId = typeof process !== 'undefined' ? process.env?.NEXT_PUBLIC_CHAIN_ID : undefined;
  return chainId === '84532' ? MINT_PRICE_PRESETS_TESTNET : MINT_PRICE_PRESETS;
}

export const MINT_PRICE_LABELS: Record<number, { tag: string; hint: string }> = {
  0.001: { tag: 'Testnet',   hint: 'Tiny amount for testing on Base Sepolia' },
  1:   { tag: 'Starter',   hint: 'Low barrier — fills fast, graduates quickly' },
  10:  { tag: 'Standard',  hint: 'Balanced price for most launches' },
  50:  { tag: 'Premium',   hint: 'Higher value tiles, larger graduation pool' },
  100: { tag: 'Exclusive', hint: 'High-conviction launches with serious backers' },
};
